import React, { useMemo } from "react";
import moment from "moment";
import Model from "@tripian/model";
import { TourSearchListFilters } from "../../hooks/useTourSearchList";

export interface TourSortOption {
  id: string;
  label: Model.TranslationKey;
  sortingBy?: string;
  sortingType?: "ascending" | "descending";
}

/**
 * Sıralama seçenekleri iki koşulla sınırlı: backend'in kabul ettiği değerler
 * (sortingBy ∈ {score, price, rating, duration, title, distance}, sortingType ∈
 * {ascending, descending}) ve modelde çeviri anahtarı bulunanlar. Puan/süreye göre
 * sıralama API tarafında mümkün ama `trips.filters.sortOptions.*` altında karşılığı
 * olmadığı için listelenmiyor — anahtarlar eklenince buraya eklenebilir.
 */
export const SORT_OPTIONS: TourSortOption[] = [
  { id: "recommended", label: "trips.filters.sortOptions.default" },
  {
    id: "price-asc",
    label: "trips.filters.sortOptions.priceLowToHigh",
    sortingBy: "price",
    sortingType: "ascending",
  },
  {
    id: "price-desc",
    label: "trips.filters.sortOptions.priceHighToLow",
    sortingBy: "price",
    sortingType: "descending",
  },
];

/**
 * Süre kovaları (dakika) — ürünlerin `duration` alanı da dakika cinsinden.
 * Etiketler moment'in yerelleştirilmiş humanize'ıyla üretiliyor; "saat" gibi bir
 * birim metnini sabit yazmak diğer dillerde bozuk görünürdü.
 */
const DURATION_BUCKETS: {
  id: string;
  minDuration?: number;
  maxDuration?: number;
}[] = [
  { id: "short", maxDuration: 120 },
  { id: "medium", minDuration: 120, maxDuration: 300 },
  { id: "long", minDuration: 300 },
];

const humanizeMinutes = (minutes: number): string =>
  moment.duration(minutes, "minutes").humanize();

const durationBucketLabel = (bucket: {
  minDuration?: number;
  maxDuration?: number;
}): string => {
  if (bucket.minDuration === undefined && bucket.maxDuration !== undefined)
    return `≤ ${humanizeMinutes(bucket.maxDuration)}`;
  if (bucket.minDuration !== undefined && bucket.maxDuration === undefined)
    return `${humanizeMinutes(bucket.minDuration)} +`;
  return `${humanizeMinutes(bucket.minDuration || 0)} – ${humanizeMinutes(
    bucket.maxDuration || 0
  )}`;
};

const PROVIDER_NAMES: Partial<Record<number, string>> = {
  [Model.PROVIDER_ID.GYG]: "GetYourGuide",
  [Model.PROVIDER_ID.VIATOR]: "Viator",
  [Model.PROVIDER_ID.CIVITATIS]: "Civitatis",
  [Model.PROVIDER_ID.TORISTY]: "Toristy",
  [Model.PROVIDER_ID.BOOK_BARBADOS]: "Bookbarbados",
  [Model.PROVIDER_ID.REZDY]: "Rezdy",
  [Model.PROVIDER_ID.GOCITY]: "GoCity",
  [Model.PROVIDER_ID.VICTORY]: "Victory Live",
  [Model.PROVIDER_ID.LIVN]: "Livn",
  [Model.PROVIDER_ID.OCCASION_GENIUS]: "OccasionGenius",
};

// Elle tutulan listede olmayan provider'lar için site config'indeki adı kullan
const providerName = (providerId: number, names: Partial<Record<number, string>>) =>
  names[providerId] ??
  window.tconfig?.PROVIDERS?.tourAndTicket?.find(p => p.id === providerId)?.name;

interface ITourFilters {
  categories: Model.TourCategory[];
  loadingCategories: boolean;
  filters: TourSearchListFilters;
  onFiltersChange: (filters: TourSearchListFilters) => void;
  onClearAll: () => void;
  t: (value: Model.TranslationKey) => string;
}

const TourFilters: React.FC<ITourFilters> = ({
  categories,
  loadingCategories,
  filters,
  onFiltersChange,
  onClearAll,
  t,
}) => {
  const availableProviders = useMemo(
    () => window.tconfig.TOUR_TICKET_PROVIDER_IDS || [],
    []
  );

  const toggleCategoryTag = (tag: string) => {
    const next = filters.categoryTags.includes(tag)
      ? filters.categoryTags.filter(item => item !== tag)
      : [...filters.categoryTags, tag];
    onFiltersChange({ ...filters, categoryTags: next });
  };

  const toggleProvider = (providerId: number) => {
    const next = filters.providerIds.includes(providerId)
      ? filters.providerIds.filter(item => item !== providerId)
      : [...filters.providerIds, providerId];
    onFiltersChange({ ...filters, providerIds: next });
  };

  const setPrice = (key: "minPrice" | "maxPrice", value: string) => {
    const parsed = value === "" ? undefined : Number(value);
    onFiltersChange({
      ...filters,
      [key]: parsed !== undefined && Number.isNaN(parsed) ? undefined : parsed,
    });
  };

  const toggleRating = (minRating: number) => {
    onFiltersChange({
      ...filters,
      minRating: filters.minRating === minRating ? undefined : minRating,
    });
  };

  const toggleDuration = (bucketId: string) => {
    const bucket = DURATION_BUCKETS.find(item => item.id === bucketId);
    if (!bucket) return;

    const isSelected =
      filters.minDuration === bucket.minDuration &&
      filters.maxDuration === bucket.maxDuration;

    onFiltersChange({
      ...filters,
      minDuration: isSelected ? undefined : bucket.minDuration,
      maxDuration: isSelected ? undefined : bucket.maxDuration,
    });
  };

  const checkbox = (
    checked: boolean,
    label: string,
    onChange: () => void,
    key: string
  ) => (
    <label
      key={key}
      className="flex items-center gap-2.5 py-1 cursor-pointer text-sm text-gray-700 hover:text-gray-900"
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="w-4 h-4 rounded border-gray-300 accent-[var(--primary-color)]"
      />
      <span className="truncate">{label}</span>
    </label>
  );

  const section = (title: string, content: React.ReactNode) => (
    <div className="py-4 border-b border-gray-100 last:border-b-0">
      {title ? (
        <h4 className="mb-2 text-sm font-semibold text-gray-800">{title}</h4>
      ) : null}
      {content}
    </div>
  );

  return (
    <div className="bg-white rounded-2xl border border-gray-200 px-4 py-2">
      <div className="flex items-center justify-between py-3 border-b border-gray-100">
        <h3 className="text-base font-semibold text-gray-900">
          {t("trips.filters.title")}
        </h3>
        <button
          type="button"
          onClick={onClearAll}
          className="text-xs px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
        >
          {t("trips.filters.clearAll")}
        </button>
      </div>

      {/* Kategoriler — /tour-api/categories'den şehir + sağlayıcıya göre gelir.
          Ayrı bir "Kategoriler" başlığı yok: API'nin döndürdüğü grup adları
          (ör. "Kültür & Tarih") zaten başlık işlevi görüyor ve modelde bu bölüm
          için bir çeviri anahtarı bulunmuyor. */}
      {section(
        "",
        loadingCategories ? (
          <div className="space-y-2">
            {[0, 1, 2, 3].map(index => (
              <div
                key={index}
                className="h-4 bg-gray-100 rounded animate-pulse"
              />
            ))}
          </div>
        ) : categories.length === 0 ? (
          <p className="text-sm text-gray-500">
            {t("trips.myTrips.localExperiences.toursEmpty")}
          </p>
        ) : (
          <div className="max-h-72 overflow-y-auto pr-1">
            {categories.map(group => (
              <div key={group.name} className="mb-3">
                <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                  {group.name}
                </div>
                {(group.subCategories || []).map(subCategory =>
                  (subCategory.categories || []).map(category =>
                    checkbox(
                      filters.categoryTags.includes(category.tag),
                      category.name,
                      () => toggleCategoryTag(category.tag),
                      `${group.name}-${category.id}`
                    )
                  )
                )}
              </div>
            ))}
          </div>
        )
      )}

      {/* Fiyat — facet aralığı API'den gelmediği için serbest sayı girişi */}
      {section(
        t("trips.myTrips.itinerary.step.poi.price"),
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            inputMode="numeric"
            value={filters.minPrice ?? ""}
            onChange={event => setPrice("minPrice", event.target.value)}
            placeholder="min"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-primary-color"
          />
          <span className="text-gray-400">–</span>
          <input
            type="number"
            min={0}
            inputMode="numeric"
            value={filters.maxPrice ?? ""}
            onChange={event => setPrice("maxPrice", event.target.value)}
            placeholder="max"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:border-primary-color"
          />
        </div>
      )}

      {/* Puan eşikleri: sağlayıcılar aynı ölçeği kullanmıyor (GYG 0-5, Civitatis
          0-10), bu yüzden eşik değerleri iki ölçekte farklı anlam taşır. */}
      {section(
        t("trips.myTrips.itinerary.step.poi.rating"),
        <div>
          {[4, 4.5].map(rating =>
            checkbox(
              filters.minRating === rating,
              `${rating}+`,
              () => toggleRating(rating),
              `rating-${rating}`
            )
          )}
        </div>
      )}

      {section(
        t("trips.myTrips.localExperiences.tourDetails.aboutThisActivity.duration"),
        <div>
          {DURATION_BUCKETS.map(bucket =>
            checkbox(
              filters.minDuration === bucket.minDuration &&
                filters.maxDuration === bucket.maxDuration,
              durationBucketLabel(bucket),
              () => toggleDuration(bucket.id),
              `duration-${bucket.id}`
            )
          )}
        </div>
      )}

      {availableProviders.length > 1 &&
        section(
          t("trips.filters.providers"),
          <div>
            {availableProviders.map(providerId =>
              checkbox(
                filters.providerIds.includes(providerId),
                providerName(providerId, PROVIDER_NAMES) || `#${providerId}`,
                () => toggleProvider(providerId),
                `provider-${providerId}`
              )
            )}
          </div>
        )}
    </div>
  );
};

export default TourFilters;
